import 'twin.macro';
import React, { useState } from 'react';
import { Checkboxes } from '@teetsh/app/src/components/Form/Checkboxes';
import { Checkbox } from '@teetsh/app/src/components/Form/Checkboxes/Checkbox';
import { Field, Form } from 'react-final-form';
import Dialog from '@teetsh/app/src/components/Dialog';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { CheckIcon as Check } from '@heroicons/react/20/solid';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useDownloadLSU } from '@teetsh/app/src/containers/Livrets/queries/useDownloadLSU';
import { useLivretEval } from '@teetsh/app/src/containers/Livrets/queries/useLivretEval';
import useEleves from '@teetsh/app/src/queries/eleves/useEleves';
import { Trans, useTranslation } from 'react-i18next';
import { Infobox } from 'components/Callout/Infobox';
import Cookies from 'js-cookie';

const SKIP_WARNING_COOKIE = 'skip_lsu_warning';

interface Props {
  livretEleveId?: string;
  livretEvalId: string;
  onSuccess: () => void;
  onClose: () => void;
}

interface FormPayload {
  livretEleveIds: string[];
}

export function Body(props: Props) {
  const { livretEleveId, livretEvalId, onSuccess, onClose } = props;
  const { t } = useTranslation(['carnet', 'common']);
  const { isDownloading, download } = useDownloadLSU({ livretEvalId });
  const {
    data: livretEval,
    isLoading,
    error,
  } = useLivretEval({
    livretEvalId,
  });
  const { data: eleves, isLoading: isLoadingEleves } = useEleves();

  const shouldSkipWarning = Cookies.get(SKIP_WARNING_COOKIE) === 'true';

  // Initialisation des states directement avec les valeurs souhaitées
  const [showWarning, setShowWarning] = useState(
    livretEleveId ? !shouldSkipWarning : false,
  );
  const [selectedIds, setSelectedIds] = useState<string[]>(
    livretEleveId ? [livretEleveId] : [],
  );
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleFinalSubmit = () => {
    if (dontShowAgain) {
      Cookies.set(SKIP_WARNING_COOKIE, 'true', { expires: 30 });
    }

    download({
      livretEleveIds: selectedIds,
    }).then(() => onSuccess());
  };

  const handleInitialSubmit = (values: FormPayload) => {
    setSelectedIds(values.livretEleveIds);

    if (shouldSkipWarning) {
      download({
        livretEleveIds: values.livretEleveIds,
      }).then(() => onSuccess());
    } else {
      setShowWarning(true);
    }
  };

  // Le useEffect n'est plus nécessaire puisque nous initialisons directement les états
  // et effectuons le téléchargement automatique dans handleAutoDownload ci-dessous

  // Fonction pour gérer le téléchargement automatique si nécessaire
  const handleAutoDownload = () => {
    if (livretEleveId && shouldSkipWarning && selectedIds.length > 0) {
      download({
        livretEleveIds: selectedIds,
      }).then(() => onSuccess());
    }
  };

  // Appel à handleAutoDownload si les conditions sont remplies
  React.useEffect(() => {
    handleAutoDownload();
  }, []);

  if (!livretEval || isLoading || error || !eleves || isLoadingEleves) {
    return null;
  }

  const livretEleveInfos = livretEval.livretsEleves
    .filter(l => (l.lsuErrors || []).length === 0)
    .map(l => {
      const matchingEleve = eleves.find(ev => ev.id === l.eleveId);
      return {
        livretEleveId: l.id,
        eleveFullName: `${matchingEleve?.familyName || ''} ${
          matchingEleve?.givenName || ''
        }`,
      };
    });

  if (
    livretEval.livretsEleves.filter(ev => ev.lsuErrors.length === 0).length ===
    0
  ) {
    return (
      <Dialog.Body>
        <p tw="text-lg">{t('carnet:livret.noLivretLSUCompatible')}</p>

        <div tw="mt-6 text-right">
          <Button intent="primary" large onClick={onClose}>
            {t('common:buttons.close')}
          </Button>
        </div>
      </Dialog.Body>
    );
  }

  if (showWarning) {
    return (
      <>
        <Dialog.Body>
          <Infobox
            tw="mb-8"
            large
            intent="warning"
            title={t('common:components.lsuTuto.warning.title')}
          >
            <div tw="my-4">
              <Trans i18nKey="common:components.lsuTuto.warning.message">
                __A__<strong>__B__</strong>__C__
              </Trans>
            </div>
            <p>{t('common:components.lsuTuto.warning.advice')}</p>
          </Infobox>
        </Dialog.Body>

        <Dialog.Footer>
          <div tw="flex grow items-center mr-auto">
            <Checkbox
              id="dontShowAgain"
              checked={dontShowAgain}
              onChange={() => setDontShowAgain(!dontShowAgain)}
              label={t('common:buttons.dontShowAgain')}
            />
          </div>
          <Button
            minimal
            large
            type="button"
            onClick={livretEleveId ? onClose : () => setShowWarning(false)}
          >
            {livretEleveId
              ? t('common:buttons.cancel')
              : t('common:buttons.back')}
          </Button>
          <Button
            large
            intent="primary"
            onClick={handleFinalSubmit}
            loading={isDownloading}
            icon={Check}
          >
            {t('common:buttons.confirm')}
          </Button>
        </Dialog.Footer>
      </>
    );
  }

  return (
    <Form<FormPayload>
      initialValues={{
        livretEleveIds: livretEleveInfos.map(l => l.livretEleveId),
      }}
      onSubmit={handleInitialSubmit}
    >
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Dialog.Body>
            <p tw="text-sm text-neutral-quiet mb-8">
              {t('carnet:livret.compatibleLSUEleveOnly')}
            </p>
            <div tw="mb-8">
              <Field
                name="livretEleveIds"
                render={({ input }) => (
                  <Checkboxes
                    value={input.value}
                    onChange={input.onChange}
                    options={livretEleveInfos.map(l => ({
                      value: l.livretEleveId,
                      label: l.eleveFullName,
                    }))}
                  />
                )}
              />
            </div>
          </Dialog.Body>
          <Dialog.Footer>
            <Button minimal large type="button" onClick={onClose}>
              {t('common:buttons.cancel')}
            </Button>
            <Button
              type="submit"
              large
              intent="primary"
              rightIcon={!shouldSkipWarning && ArrowRightIcon}
            >
              {shouldSkipWarning
                ? t('common:buttons.confirm')
                : t('common:buttons.next')}
            </Button>
          </Dialog.Footer>
        </form>
      )}
    </Form>
  );
}
